import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router'

import { bookingPath, type BookingPage } from '../../api/transport'
import { PageBand, PageShell } from '../../components/PageShell'
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'
import { formatPesewas } from '../../lib/money'
import {
  MEDCOR_REGISTERED,
  paymentMethodWords,
  statusWords,
  whenLabel,
} from './bookingWords'
import { Desk } from './Desk'
import { useFocusOnMount } from './focus'
import { SERVICE_NAME } from './services'
import { TransportHeader } from './TransportHeader'
import { useBookingFromLink } from './useBookingFromLink'

/**
 * The receipt (M40-T10, spec §C.3.7, **Decision 2 statement 4**), behind the
 * same private link as the booking page. Printable: the print stylesheet
 * keeps the receipt and drops the page's chrome.
 */

export function TransportReceipt(): ReactNode {
  const { reference = '' } = useParams()
  const { state } = useBookingFromLink(reference)
  const heading = useFocusOnMount<HTMLHeadingElement>()

  return (
    <PageShell measure={640} as="main">
      <div className="print:hidden">
        <TransportHeader />
      </div>
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-5"
      >
        <h1
          ref={heading}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          {state.kind === 'missing'
            ? 'Receipt not found'
            : `Receipt — ${reference}`}
        </h1>
        {state.kind === 'loading' && (
          <p role="status" className="text-[13px] text-ink-3">
            Opening your receipt…
          </p>
        )}
        {state.kind === 'missing' && (
          <p role="alert" className="text-[14px] leading-relaxed text-ink-1">
            We can&apos;t find that booking. Check the link, or call the
            transport desk
            <Desk />.
          </p>
        )}
        {state.kind === 'ready' && (
          <ReceiptBody page={state.page} manageKey={state.manageKey} />
        )}
      </PageBand>
    </PageShell>
  )
}

function Row({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): ReactNode {
  return (
    <div className="flex justify-between gap-3 py-1">
      <dt className="text-ink-2">{label}</dt>
      <dd className="text-right text-ink-1">{children}</dd>
    </div>
  )
}

function ReceiptBody({
  page,
  manageKey,
}: {
  page: BookingPage
  manageKey: string
}): ReactNode {
  const { receipt } = page
  const words = statusWords(page.status)
  return (
    <>
      {words && (
        <p className="rounded-r3 border border-line-2 bg-bg-soft px-3 py-2.5 text-[13px] font-medium text-ink-1">
          {words}
        </p>
      )}
      <dl className="flex flex-col divide-y divide-line-1 text-[13px]">
        <Row label="Reference">{page.reference}</Row>
        <Row label="Booked by">{page.booker_first_name}</Row>
        <Row label="Passenger">
          {page.passenger_first_name ?? page.booker_first_name}
        </Row>
        <Row label="Service">{SERVICE_NAME[page.service]}</Row>
        <Row label="Date and time">{whenLabel(page.pickup_at)}</Row>
        <Row label="Trip">
          {page.pickup_area} → {page.hospital}
        </Row>
      </dl>

      <section aria-labelledby="receipt-items" className="flex flex-col gap-1">
        <h2
          id="receipt-items"
          className="font-mono text-[10px] uppercase tracking-wide text-ink-3"
        >
          Items
        </h2>
        <dl className="flex flex-col gap-1 text-[13px]">
          {receipt.fare_lines.map((line, i) => (
            <div
              key={`${line.item}-${i}`}
              className="flex justify-between gap-3"
            >
              <dt className="text-ink-2">
                {line.free ? `${line.label} — Free` : line.label}
              </dt>
              <dd className="tabular-nums text-ink-1">
                {line.free ? '' : formatPesewas(line.amount_pesewas)}
              </dd>
            </div>
          ))}
          <div className="mt-1 flex justify-between gap-3 border-t border-line-1 pt-1.5 font-semibold text-ink-1">
            <dt>Total</dt>
            <dd className="tabular-nums">
              {formatPesewas(receipt.total_pesewas)}
            </dd>
          </div>
        </dl>
      </section>

      <dl className="flex flex-col divide-y divide-line-1 text-[13px]">
        <Row label="Payment method">
          {receipt.payment_method
            ? paymentMethodWords(receipt.payment_method)
            : '—'}
        </Row>
        <Row label="Paid">
          {receipt.paid_at ? whenLabel(receipt.paid_at) : '—'}
        </Row>
      </dl>

      <section
        aria-labelledby="receipt-policy"
        className="text-[12.5px] text-ink-2"
      >
        <h2 id="receipt-policy" className="font-medium text-ink-1">
          Cancellation
        </h2>
        <p>
          Free changes up to 24 hrs before pickup. Cancel more than 24 hrs
          ahead: refund minus admin fee. Within 24 hrs: 50% refund. If Medcor
          cannot provide your trip, you get a full refund automatically. To
          change or cancel, call the transport desk
          <Desk />.
        </p>
      </section>

      <p className="rounded-r3 border-l-[3px] border-sos bg-sos-bg px-3 py-2.5 text-[12.5px] leading-relaxed text-ink-1">
        Medcor Transport is a scheduled, non-emergency service. It cannot be
        used in a medical emergency. In an emergency, call {EMERGENCY_NUMBER}.
      </p>

      <address className="text-[12px] not-italic text-ink-3">
        {MEDCOR_REGISTERED.name}
        <br />
        {MEDCOR_REGISTERED.address}
      </address>

      <div className="flex flex-wrap gap-2 print:hidden">
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex min-h-[44px] items-center rounded-r3 border border-line-2 px-3 text-[13px] font-semibold text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Print
        </button>
        <Link
          to={bookingPath(page.reference, manageKey)}
          className="inline-flex min-h-[44px] items-center rounded-r3 border border-line-2 px-3 text-[13px] font-semibold text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Back to your booking
        </Link>
      </div>
    </>
  )
}
